import { useEffect, useState } from 'react'
import { gifsApi, type Gif, type GifCategory } from '@/lib/gifsApi'
import styles from './MediaPicker.module.css'

interface Props {
  query: string
  /** Клик по плитке категории подставляет её запрос в поиск. */
  onQuery: (query: string) => void
  onPick: (gif: Gif) => void
}

/**
 * Содержимое вкладки «Гифки». Пустой запрос показывает категории и что смотрят сейчас,
 * непустой — поиск. Запросы уходят в наш API: ключ Klipy не покидает сервер.
 */
export function GifGrid({ query, onQuery, onPick }: Props) {
  // состояние меняем только из ответа сети: ключ показывает, к какому запросу оно относится
  const [state, setState] = useState<{ key: string; items: Gif[]; error: string | null }>({ key: '\u0000', items: [], error: null })
  const [categories, setCategories] = useState<GifCategory[]>([])
  const q = query.trim()
  const loading = state.key !== q

  useEffect(() => {
    let cancelled = false
    // печатают быстрее, чем отвечает сеть: ждём паузу в наборе
    const timer = setTimeout(
      () => {
        const request = q ? gifsApi.search(q) : gifsApi.trending()
        request
          .then((page) => {
            if (!cancelled) setState({ key: q, items: page.items, error: null })
          })
          .catch(() => {
            if (!cancelled) setState({ key: q, items: [], error: 'Гифки сейчас недоступны' })
          })
      },
      q ? 300 : 0,
    )
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [q])

  useEffect(() => {
    let cancelled = false
    gifsApi
      .categories()
      .then((r) => {
        if (!cancelled) setCategories(r.categories)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const { items, error } = state

  if (error && !loading) return <p className={styles.empty}>{error}</p>

  return (
    <>
      {!q && categories.length ? (
        <div className={styles.group}>
          <div className={styles.groupTitle}>Категории</div>
          <div className={styles.categoryGrid}>
            {categories.slice(0, 8).map((c) => (
              <button key={c.query} type="button" className={styles.category} onClick={() => onQuery(c.query)}>
                <img src={c.preview} alt="" className={styles.categoryImg} draggable={false} loading="lazy" />
                <span className={styles.categoryName}>{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className={styles.group}>
        <div className={styles.groupTitle}>{q ? 'Найдено' : 'Популярное'}</div>
        {loading ? <p className={styles.loading}>Загружаем…</p> : null}
        {!loading && items.length === 0 ? <p className={styles.empty}>Ничего не найдено</p> : null}
        {!loading && items.length ? (
          <div className={styles.gifGrid}>
            {items.map((g) => (
              <button key={g.id} type="button" className={styles.gifCell} data-tooltip={g.title} onClick={() => onPick(g)}>
                <img
                  src={g.preview.url}
                  alt={g.title}
                  className={styles.gifImg}
                  style={{ aspectRatio: g.preview.width && g.preview.height ? `${g.preview.width} / ${g.preview.height}` : undefined }}
                  draggable={false}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </>
  )
}
